//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "#webapp/src/__schema__";
import ReactTooltip from "react-tooltip";
// libraries
import * as React from "react";
import { createRefetchContainer } from "react-relay";
// team-settings-admin-slack imports
import { refetchQuery, teamSettingsAdminSlackFragments } from "./TeamSettingsAdminSlack.gql";
import { styles } from "./TeamSettingsAdminSlack.styles";
import { IContainer, IProps, IState } from "./TeamSettingsAdminSlackTypes";
// other imports
import { SlackButton } from "#webapp/src/components/slack-button";
import { SlackChannelPicker } from "#webapp/src/components/slack-channel-picker";
import { H1, P } from "#webapp/src/components/ui";
import { CalloutBox } from "@clockwise/web-commons/src/components/callout-box/CalloutBox.tailwind";
// util
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { logger } from "#webapp/src/util/logger.util";
// constants
import { timeSlots } from "#webapp/src/constants/time-slots.constant";
// state
import {
  saveTeamSettings,
  updateSlackAccountForTeam,
  validateSlackChannelBySendingMessage,
} from "#webapp/src/mutations";
// material-ui
import { Button, ButtonSet, Select, SelectOption } from "@clockwise/design-system";
import { Delete, Warning } from "@clockwise/design-system/icons";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
// styles
import { TimeSlotEnum } from "@clockwise/schema";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import { map } from "lodash";
import toast from "react-hot-toast";

//////////////////
// COMPONENT
//////////////////
class TeamSettingsAdminSlackCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const slackAccount = this.maybeGetSlackAccount(props);
    const slackSettings = this.maybeGetSlackSettings(props);

    if (!slackSettings) {
      logger.error("Failed to fetch slack settings");
    }

    const slackChannel = slackSettings
      ? ({
          channelId: slackSettings.channelId,
          name: slackSettings.channelName,
        } as ISchema.ISlackChannel)
      : undefined;
    this.state = {
      authingSlack: false,
      slackNotificationEnabled: !!slackAccount,
      slackChannel: slackChannel,
      slackNotificationTimeSlot:
        slackSettings && slackSettings.timeSlot
          ? slackSettings.timeSlot
          : ISchema.TimeSlotEnum.T_09_00,
      showSlackDialog: false,
      showRemoveSlackDialog: false,
      savingSlackSettings: false,
      slackChannelError: !slackChannel ? "unselected" : undefined,
    };
  }

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private onRemoveSlackDialogClose = () => {
    this.setState({ showRemoveSlackDialog: false });
  };

  private onSlackSettingsDialogClose = () => {
    this.setState({ showSlackDialog: false });
  };

  private onSlackSettingsDialogConfirm = () => {
    const successCallback = () => {
      toast.success(`Set Slack notification preferences`);
      this.onSlackSettingsDialogClose();
    };

    const failureCallback = () => {
      const msg = `Failed to set Slack notification preferences`;
      toast.error(msg);
      logger.error(msg);
    };

    this.maybeValidateSlackChannelAndUpdateTeamSettings(successCallback, failureCallback);
  };

  private onSlackAuthClick = () => {
    // this.setState({ authingSlack: true });
  };

  private onSlackAuthPopupClosed = () => {
    const { org, team } = this.props;
    this.props.relay.refetch(
      { orgRelayId: org.id, teamId: team.teamId },
      undefined,
      () => {
        // after close, refetch and see if we are authed in
        // set toggle based on that
        const slackAccount = this.maybeGetSlackAccount(this.props);
        // autoselect a slack channel
        // const slackList = this.props.org.publicChannelsForSlackForTeam.__typename === 'SlackChannelList' ? this.props.org.publicChannelsForSlackForTeam.list : [];
        // const autoSelectedSlackChannelId = slackList.length > 0 ? slackList[0].channelId : '';
        this.setState(
          {
            authingSlack: false,
            slackNotificationEnabled: !!slackAccount,
            showSlackDialog: !!slackAccount,
          },
          () => {
            this.maybeValidateSlackChannelAndUpdateTeamSettings();
          },
        );
      },
      { force: true },
    );
  };

  private onRemoveSlackAccount = () => {
    const { relay, org, team } = this.props;

    this.setState({ slackNotificationEnabled: false, slackChannel: undefined });
    updateSlackAccountForTeam(
      relay.environment,
      {
        orgRelayId: org.id,
        teamId: team.teamId,
        action: ISchema.SlackForTeamUpdateAction.Delete,
      },
      () => {
        toast.success("Removed Slack notifications");
        this.onRemoveSlackDialogClose();
      },
      () => {
        const msg = `Failed to remove Slack notifications`;
        toast.error(msg);
        logger.error(msg);
      },
    );

    track(TrackingEvents.SLACK.TEAMS_REMOVED_SLACK);
  };

  private onClickSaveSlackSettings = () => {
    const successCallback = () => {
      toast.success(`Set Slack notification preferences`);
    };

    const failureCallback = () => {
      const msg = `Failed to set Slack notification preferences`;
      toast.error(msg);
      logger.error(msg);
    };

    this.maybeValidateSlackChannelAndUpdateTeamSettings(successCallback, failureCallback);
  };

  private onChangeSlackChannel = (
    slackChannel: ISchema.ISlackChannel,
    postOAuthModal?: boolean,
  ) => {
    this.setState({ slackChannel, slackChannelError: undefined });

    track(TrackingEvents.SLACK.SLACK_CHANNEL_PICKER_SELECT, {
      channelId: slackChannel.channelId,
      channelName: slackChannel.name,
      postOAuthModal,
    });
  };

  private onChangeSlackDailyReminder = (
    slackNotificationTimeSlot: TimeSlotEnum,
    postOAuthModal?: boolean,
  ) => {
    this.setState({ slackNotificationTimeSlot });

    track(TrackingEvents.SLACK.TEAMS_CHANGED_DAILY_REMINDER_TIME, { postOAuthModal });
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private maybeGetSlackAccount = (props: IProps) => {
    if (!props.org.slackAccountForTeamErrorable) {
      return undefined;
    }

    const slackAccount =
      props.org.slackAccountForTeamErrorable.__typename === "SlackAccount"
        ? props.org.slackAccountForTeamErrorable
        : undefined;

    if (!slackAccount) {
      logger.error("Failed to load slackAccountForTeam");
    }

    return slackAccount;
  };

  private maybeGetSlackSettings = (props: IProps) => {
    const { org } = props;
    const slackSettings =
      org.teamSettings.__typename === "TeamSettings" ? org.teamSettings.slackSettings : undefined;
    return slackSettings;
  };

  private isStateSlackChannelSameAsSaved = () => {
    const { slackChannel } = this.state;
    const slackSettings = this.maybeGetSlackSettings(this.props);

    return (slackSettings && slackSettings.channelId) === (slackChannel && slackChannel.channelId);
  };

  private isStateSlackNotificationTimeslotSameAsSaved = () => {
    const { slackNotificationTimeSlot } = this.state;
    const slackSettings = this.maybeGetSlackSettings(this.props);

    return (slackSettings && slackSettings.timeSlot) === slackNotificationTimeSlot;
  };

  private maybeValidateSlackChannelAndUpdateTeamSettings = (
    successCallback?: () => void,
    failureCallback?: () => void,
  ) => {
    const { relay, org, team } = this.props;
    const { slackChannel } = this.state;

    this.setState({ savingSlackSettings: true });

    if (!slackChannel || this.isStateSlackChannelSameAsSaved()) {
      return this.updateTeamSettings(successCallback, failureCallback);
    }

    const slackChannelId = slackChannel ? slackChannel.channelId : "";
    const slackChannelName =
      slackChannel && slackChannelId.startsWith("CustomSlackEntry-")
        ? slackChannelId.split("CustomSlackEntry-")[1]
        : slackChannel && slackChannel.name
        ? slackChannel.name
        : "";

    validateSlackChannelBySendingMessage(
      relay.environment,
      { slackChannelName, orgRelayId: org.id, teamId: team.teamId },
      (response: ISchema.IValidateSlackChannelBySendingMessageMutationPayload) => {
        const { isValid, channel } = response.validateSlackChannelResponse;
        if (isValid) {
          this.setState({ slackChannel: channel, slackChannelError: undefined }, () => {
            this.updateTeamSettings(successCallback, failureCallback);
          });
        } else {
          this.setState({ slackChannelError: "invalid" });
          toast.error(`We couldn't find a public Slack channel with this name. Try again!`);
        }
        this.setState({ savingSlackSettings: false });
      },
      () => {
        logger.error("failed to validateSlackChannelBySendingMessage");
        if (failureCallback) {
          failureCallback();
        }
      },
    );
  };

  private updateTeamSettings = (successCallback?: () => void, failureCallback?: () => void) => {
    const { relay, org, team } = this.props;
    const { slackNotificationEnabled, slackChannel, slackNotificationTimeSlot } = this.state;

    this.setState({ savingSlackSettings: true });
    const slackChannelId = slackChannel ? slackChannel.channelId : undefined;
    const slackChannelName = slackChannel ? slackChannel.name : undefined;

    // patch settings
    const input = {
      orgRelayId: org.id,
      teamId: team.teamId,
      slackNotificationEnabled,
      slackChannelId: slackChannelId,
      slackChannelName: slackChannelName,
      slackNotificationTimeSlot: slackNotificationTimeSlot,
    };

    const onSuccess = () => {
      this.setState({ savingSlackSettings: false });
      if (successCallback) {
        successCallback();
      }
    };

    const onFailure = () => {
      this.setState({ savingSlackSettings: false });
      if (failureCallback) {
        failureCallback();
      }
    };

    saveTeamSettings(relay.environment, input, onSuccess, onFailure);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderSlackChannelError = (postOAuthModal?: boolean) => {
    const { slackChannelError, slackChannel } = this.state;

    if (!slackChannelError) {
      return "";
    }

    if (!postOAuthModal && slackChannelError === "unselected") {
      return "";
    }

    const slackAccount = this.maybeGetSlackAccount(this.props);
    return (
      <div className="cw-mb-4">
        <CalloutBox
          sentiment="warning"
          icon={<Warning className="cw-text-warning" />}
          body={
            <span className="cw-body-base cw-text-warning">
              {slackChannelError === "unselected"
                ? `Your team won't receive a daily summary in Slack until a Slack channel is chosen below.`
                : slackChannelError === "invalid"
                ? `Oops! we couldn't find channel "${
                    slackChannel ? slackChannel.name : ""
                  }" in the ${
                    slackAccount ? slackAccount.teamDisplayName : "your team"
                  } workspace. Please check if you have the right channel name.`
                : ""}
            </span>
          }
        />
      </div>
    );
  };
  private maybeRenderRemoveSlackDialog = () => {
    const { showRemoveSlackDialog } = this.state;

    return (
      <Dialog
        open={showRemoveSlackDialog}
        onClose={this.onRemoveSlackDialogClose}
        PaperProps={{ style: { maxWidth: 700, width: 700 } }}
      >
        <DialogTitle>
          <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0, textAlign: "center" }}>
            Remove Slack for everyone?
          </H1>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
          <P
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: greys.slate,
              marginBottom: 30,
              textAlign: "center",
            }}
          >
            Are you sure you want to remove Slack for everyone on this team? This action cannot be
            undone.
          </P>
        </DialogContent>
        <DialogActions>
          <ButtonSet>
            <Button variant="text" onClick={this.onRemoveSlackDialogClose}>
              Cancel
            </Button>
            <Button variant="text" sentiment="destructive" onClick={this.onRemoveSlackAccount}>
              Remove Slack
            </Button>
          </ButtonSet>
        </DialogActions>
      </Dialog>
    );
  };

  private renderSlackSettings = (postOAuthModal?: boolean) => {
    const { org } = this.props;
    const {
      slackNotificationEnabled,
      slackChannel,
      slackNotificationTimeSlot,
      savingSlackSettings,
      slackChannelError,
    } = this.state;

    return (
      <React.Fragment>
        <div className="cw-grid cw-grid-cols-2 cw-gap-4">
          <SlackChannelPicker
            org={org}
            onChange={(c) => this.onChangeSlackChannel(c, postOAuthModal)}
            value={slackChannel && slackChannel.channelId ? slackChannel : undefined}
            disabled={!slackNotificationEnabled}
            error={!!slackChannelError}
          />
          <Select
            label="Time to receive daily reminder"
            value={slackNotificationTimeSlot}
            onChange={(timeSlot: TimeSlotEnum) =>
              this.onChangeSlackDailyReminder(timeSlot, postOAuthModal)
            }
            name="Daily Reminder Time"
            fullWidth
          >
            {map(timeSlots.min, (formattedSlot, slot) => (
              <SelectOption key={slot} value={slot}>
                {formattedSlot}
              </SelectOption>
            ))}
          </Select>
        </div>
        {postOAuthModal ? (
          <div className="cw-flex cw-items-center cw-justify-between cw-gap-2 cw-mt-4">
            <Button
              onClick={this.onClickSaveSlackSettings}
              sentiment="positive"
              disabled={
                (this.isStateSlackNotificationTimeslotSameAsSaved() &&
                  this.isStateSlackChannelSameAsSaved()) ||
                savingSlackSettings
              }
            >
              Save
            </Button>
            <Button
              variant="outlined"
              onClick={() => this.setState({ showRemoveSlackDialog: true })}
              startIcon={Delete}
              disabled={savingSlackSettings}
            >
              Remove Slack account
            </Button>
          </div>
        ) : (
          ""
        )}
      </React.Fragment>
    );
  };

  private maybeRenderSlackDialog = () => {
    const { showSlackDialog, slackChannel, slackNotificationTimeSlot } = this.state;

    return (
      <Dialog
        open={showSlackDialog}
        onClose={this.onSlackSettingsDialogClose}
        PaperProps={{ style: { maxWidth: 700, width: 700 } }}
      >
        <DialogTitle>
          <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
            Next up, set up the integration
          </H1>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
          <P
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: greys.slate,
              marginBottom: 15,
            }}
          >
            You have successfully connected Clockwise to Slack! Set up the integration to start
            receiving a daily summary of events on your team availability calendar.
          </P>
          {this.maybeRenderSlackChannelError()}
          <div style={{ marginTop: 30 }}>{this.renderSlackSettings()}</div>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <ButtonSet>
            <Button variant="text" onClick={this.onSlackSettingsDialogClose}>
              Cancel
            </Button>
            <Button
              variant="text"
              sentiment="positive"
              onClick={this.onSlackSettingsDialogConfirm}
              disabled={!slackChannel || !slackNotificationTimeSlot}
            >
              Confirm
            </Button>
          </ButtonSet>
        </DialogActions>
      </Dialog>
    );
  };

  public render() {
    const { classes, org, team, isWebSettings } = this.props;
    const { slackNotificationEnabled, authingSlack, showSlackDialog } = this.state;
    const slackAccount = this.maybeGetSlackAccount(this.props);

    const manageTeamCalendar =
      org.teamSettings.__typename === "TeamSettings"
        ? org.teamSettings.manageTeamCalendar
        : undefined;
    const toolTipId = "team-settings-admin-slack-tooltip";

    return (
      <React.Fragment>
        <div className={classes.slackForm}>
          {!isWebSettings && (
            <>
              <div className="cw-heading-lg cw-mb-2">
                Connect team availability calendar with Slack
              </div>
              <div className={classes.description}>
                Clockwise sends a daily summary of events on the team availability calendar to your
                selected Slack channel.
              </div>
            </>
          )}
          <div
            style={
              !manageTeamCalendar
                ? {
                    cursor: "not-allowed",
                    opacity: 0.4,
                    filter: "greyscale(100%)",
                    width: "fit-content",
                  }
                : {}
            }
            data-for={toolTipId}
            data-tip
          >
            <div style={!manageTeamCalendar ? { pointerEvents: "none" } : {}}>
              {authingSlack || showSlackDialog ? (
                "Authenticating..."
              ) : !slackNotificationEnabled ? (
                <SlackButton
                  onAuthPopupClosed={this.onSlackAuthPopupClosed}
                  teamId={team.teamId}
                  isTeamsSlack
                  onClick={this.onSlackAuthClick}
                />
              ) : (
                ""
              )}
              {slackNotificationEnabled && !showSlackDialog ? (
                <div className={classes.slackSettings}>
                  <div className="cw-heading-base cw-mb-2">
                    {slackAccount ? slackAccount.teamDisplayName : ""}
                  </div>
                  {this.maybeRenderSlackChannelError(true)}
                  {this.renderSlackSettings(true)}
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
          {!manageTeamCalendar ? (
            <ReactTooltip id={toolTipId} effect="solid">
              Please first enable the team calendar to use this feature
            </ReactTooltip>
          ) : (
            ""
          )}
        </div>
        {this.maybeRenderSlackDialog()}
        {this.maybeRenderRemoveSlackDialog()}
      </React.Fragment>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
const TeamSettingsAdminSlackStyled = withStyles(styles)(TeamSettingsAdminSlackCmpt);

export const TeamSettingsAdminSlack = createRefetchContainer<IContainer>(
  TeamSettingsAdminSlackStyled,
  teamSettingsAdminSlackFragments,
  refetchQuery,
);
